import type { ModulesEnabled } from "@/features/manifest/model/types";

/** Tenant manifest module flag → permission matrix module key. */
export const MANIFEST_TO_PERMISSION_MODULE: Partial<
  Record<keyof ModulesEnabled | "scheduling" | "facilities", string>
> = {
  casework: "cases",
  people: "members",
  tasks: "tasks",
  checkin: "checkin",
  inventory: "inventory",
  programs: "programs",
  grant_management: "grants",
  accounting: "accounting",
  scheduling: "scheduling",
  // Facilities rides the same tenant flag as scheduling - a tenant with a
  // schedule has rooms - but it is its own permission row, so looking after
  // the estate can be given to one person without giving them the rota.
  facilities: "facilities",
  volunteer_hours: "volunteer_hours",
  resource_directory: "resource_directory",
  ai_copilot: "copilot",
};

export type PermissionCapability =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "assign"
  | "approve"
  | "export"
  | "view_pricing"
  | "see_clients";

export function capabilityField(cap: PermissionCapability): string {
  return `can_${cap}`;
}

/*
 * These three lists are a FALLBACK, used only while
 * GET /manifest/permissions/capabilities/ is in flight. The server derives
 * the real answer from its endpoints and has a test that fails when the two
 * drift; this copy has no such guard, which is exactly how it came to claim
 * approve on cases (never implemented) while hiding export on accounting,
 * members and vendors.
 *
 * If you are tempted to add a module here, add the endpoint instead and let
 * the server say so.
 */

/** Modules where approve is enforced in the product (workflow approval). */
export const MODULES_WITH_APPROVE_PERMISSION = [
  "accounting",
  "inventory",
  "submission_center",
] as const;

/** Modules where export is enforced in the product (CSV/XLSX export). */
export const MODULES_WITH_EXPORT_PERMISSION = [
  "accounting",
  "cases",
  "data",
  "inventory",
  "members",
  "reports",
  "scheduling",
  "vendors",
  "resource_directory",
] as const;

/** Modules where assign is enforced: shift assignment, and handing a task to
 *  somebody else. Everywhere else the column is decorative. */
export const MODULES_WITH_ASSIGN_PERMISSION = ["scheduling", "tasks"] as const;

/** Modules where view_pricing is enforced (inventory valuation). */
export const MODULES_WITH_VIEW_PRICING_PERMISSION = ["inventory"] as const;

export const MODULES_WITH_SEE_CLIENTS_PERMISSION = ["members"] as const;

export function isCapabilityConfigurable(
  module: string,
  cap: PermissionCapability,
): boolean {
  if (cap === "view_pricing") {
    return MODULES_WITH_VIEW_PRICING_PERMISSION.includes(
      module as (typeof MODULES_WITH_VIEW_PRICING_PERMISSION)[number],
    );
  }
  if (cap === "see_clients") {
    return MODULES_WITH_SEE_CLIENTS_PERMISSION.includes(
      module as (typeof MODULES_WITH_SEE_CLIENTS_PERMISSION)[number],
    );
  }
  if (cap === "approve") {
    return MODULES_WITH_APPROVE_PERMISSION.includes(
      module as (typeof MODULES_WITH_APPROVE_PERMISSION)[number],
    );
  }
  if (cap === "export") {
    return MODULES_WITH_EXPORT_PERMISSION.includes(
      module as (typeof MODULES_WITH_EXPORT_PERMISSION)[number],
    );
  }
  if (cap === "assign") {
    return MODULES_WITH_ASSIGN_PERMISSION.includes(
      module as (typeof MODULES_WITH_ASSIGN_PERMISSION)[number],
    );
  }
  return true;
}
